import { useEffect, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";

function maskToRegex(value) {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  if (raw.startsWith("^") || raw.endsWith("$") || raw.includes("[")) return raw;
  const escaped = raw.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
  let out = "";
  for (const ch of escaped) {
    if (ch === "x" || ch === "X") out += "[0-9]";
    else if (ch === "*") out += ".*";
    else out += ch;
  }
  return `^${out}$`.replace(/(?:\[0-9\]){2,}/g, (m) => `[0-9]{${m.length / 5}}`);
}

function regexToMask(value) {
  let raw = String(value || "");
  const simple = raw.match(/^\^([0-9]+)(?:\[0-9\]|\[0-9\]\{(\d+)\})\$$/);
  if (simple) return simple[1] + "x".repeat(simple[2] ? Number(simple[2]) : 1);
  raw = raw.replace(/^\^/, "").replace(/\$$/, "");
  raw = raw.replace(/\[0-9\]\{(\d+)\}/g, (_, n) => "x".repeat(Number(n)));
  raw = raw.replace(/\[0-9\]/g, "x").replace(/\.\*/g, "*");
  return raw;
}

function PatternPreview({ value }) {
  const mask = regexToMask(value);
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-sm">{mask}</span>
      {mask !== value && <span className="font-mono text-[10px] text-[color:var(--text-muted)]">{value}</span>}
    </div>
  );
}

export default function Rotas() {
  const [ops, setOps] = useState([]);
  const [ipbxs, setIpbxs] = useState([]);
  const [anuncios, setAnuncios] = useState([]);
  const [dids, setDids] = useState([]);
  useEffect(() => {
    (async () => {
      const [o, i, a, d] = await Promise.all([api.get("/operadoras"), api.get("/ipbxs"), api.get("/anuncios"), api.get("/dids")]);
      setOps(o.data); setIpbxs(i.data); setAnuncios(a.data); setDids(d.data);
    })();
  }, []);

  const opMap = Object.fromEntries(ops.map(o => [o.id, o.nome]));
  const ipbxMap = Object.fromEntries(ipbxs.map(i => [i.id, i.nome]));
  const anuncioMap = Object.fromEntries(anuncios.map(a => [a.id, a.nome]));

  return (
    <CrudPage
      title="Rotas / LCR"
      subtitle="Least Cost Routing"
      endpoint="/rotas"
      testidPrefix="rotas"
      headers={["Nome", "Padrão", "Operadora", "IPBX", "Anúncio", "DID enviado", "Prioridade", "Custo", "Fallback", "Horário", "Status"]}
      renderRow={(r) => (
        <>
          <td className="font-medium">{r.nome}</td>
          <td><PatternPreview value={r.padrao} /></td>
          <td>{opMap[r.operadora_id] || <span className="text-[color:var(--text-muted)]">-</span>}</td>
          <td>{ipbxMap[r.ipbx_id] || <span className="text-[color:var(--text-muted)]">-</span>}</td>
          <td>{anuncioMap[r.anuncio_id] || <span className="text-[color:var(--text-muted)]">-</span>}</td>
          <td className="font-mono text-xs">{r.did_saida || "padrão IPBX"}</td>
          <td className="font-mono">{r.prioridade}</td>
          <td className="font-mono">R$ {Number(r.custo).toFixed(2)}</td>
          <td>{r.fallback_on_fail !== false ? <span className="sbc-tag tag-online">ativo</span> : <span className="sbc-tag tag-offline">não</span>}</td>
          <td className="font-mono text-xs">{r.horario_inicio || r.horario_fim ? `${r.horario_inicio || "00:00"}-${r.horario_fim || "23:59"}` : "24h"}</td>
          <td>{r.enabled
            ? <span className="sbc-tag tag-online">ativa</span>
            : <span className="sbc-tag tag-offline">inativa</span>}
          </td>
        </>
      )}
      defaultValues={{ nome: "", padrao: "071xxxx", operadora_id: "", ipbx_id: "", anuncio_id: "", did_saida: "", prioridade: 100, custo: 0.0, fallback_on_fail: true, horario_inicio: "", horario_fim: "", dias_semana: "1-7", enabled: true }}
      transformFormOpen={(form) => ({ ...form, padrao: regexToMask(form.padrao) })}
      transformSubmit={(form) => ({ ...form, padrao: maskToRegex(form.padrao) })}
      fields={[
        { key: "nome", label: "Nome", full: true, placeholder: "Ex: Nacional Vivo" },
        { key: "padrao", label: "Destino", mono: true, full: true, placeholder: "071xxxx", hint: "Use mascara simples: 071xxxx, 55xxxxxxxxxxx ou 0800*. Regex avancado ainda funciona se comecar com ^." },
        { key: "operadora_id", label: "Operadora", type: "select",
          options: [{ value: "", label: "-- selecione --" }, ...ops.map(o => ({ value: o.id, label: o.nome }))] },
        { key: "ipbx_id", label: "IPBX destino", type: "select",
          options: [{ value: "", label: "-- opcional --" }, ...ipbxs.map(i => ({ value: i.id, label: i.nome }))] },
        { key: "anuncio_id", label: "Anúncio antes do encaminhamento", type: "select",
          options: [{ value: "", label: "Sem anúncio" }, ...anuncios.map(a => ({ value: a.id, label: a.nome }))] },
        { key: "did_saida", label: "DID enviado ao IPBX", type: "select", options: [{ value: "", label: "Usar padrao do IPBX" }, ...dids.filter(d => d.enabled !== false).map(d => ({ value: d.numero, label: `${d.numero} - ${opMap[d.operadora_id] || "sem operadora"}` }))], hint: "Cadastre os numeros no menu DIDs." },
        { key: "prioridade", label: "Prioridade (menor = maior)", type: "number", mono: true },
        { key: "custo", label: "Custo por minuto", type: "number", mono: true },
        { key: "fallback_on_fail", label: "Fallback automático", type: "toggle" },
        { key: "horario_inicio", label: "Horrio início", placeholder: "08:00", mono: true, hint: "Opcional; use HH:MM." },
        { key: "horario_fim", label: "Horário fim", placeholder: "18:00", mono: true },
        { key: "dias_semana", label: "Dias da semana", placeholder: "1-5", mono: true, hint: "1=segunda, 7=domingo. Ex: 1-5 ou 1-7." },
        { key: "enabled", label: "Ativada", type: "toggle" },
      ]}
    />
  );
}
